import React from "react";
import { Helmet } from "react-helmet-async";

const BASE_URL = "https://darknetsearch.com";

// Default SEO content for different languages
const DEFAULT_SEO_CONTENT = {
  en: {
    title: "Dark Web Monitoring & Cyber Threat Solution | DarknetSearch",
    description: "DarknetSearch delivers real-time cyber threat intelligence by monitoring exposed data, credentials, and leaks across the dark web. Detect threats early and protect your business."
  },
  es: {
    title: "Monitoreo Dark Web & Solución CTI | DarknetSearch",
    description: "Detecta filtraciones de datos en la dark web en tiempo real. DarknetSearch te ayuda a proteger tu empresa frente a amenazas cibernéticas con alertas inteligentes y análisis avanzados de brechas de seguridad."
  },
  fr: {
    title: "Surveillance du Dark Web & Solution CTI | DarknetSearch",
    description: "Détectez les fuites de données sur le dark web en temps réel. DarknetSearch vous aide à protéger votre entreprise contre les menaces cybernétiques grâce à des alertes intelligentes et une analyse avancée des violations de sécurité."
  },
  de: {
    title: "Dark Web Überwachung & Threat Intelligence Plattform | DarknetSearch",
    description: "DarknetSearch liefert Cyber Threat Intelligence in Echtzeit, indem es exponierte Daten, Zugangsdaten und Leaks im Dark Web überwacht. Erkennen Sie Bedrohungen frühzeitig und schützen Sie Ihr Unternehmen."
  },
  glossary: {
    title: "Cybersecurity Glossary: Key Terms You Need to Know | DarknetSearch",
    description: "Discover essential cybersecurity terms in our complete glossary. Learn the meaning of key concepts like phishing, APTs, malware, and more."
  }
};
const PAGE_SEO_CONTENT = {
  contact: {
    meta_title: "Contact us | Darknetsearch",
    meta_description: "Connect with our team and stay updated on the latest dark web threats and cybersecurity insights from DarknetSearch"
  },
  documentation: {
    meta_title: "Product Documentation | DarknetSearch",
    meta_description: "Explore DarknetSearch documentation: monitoring coverage, alerts, integrations, exports, and automation across dark web, deep web, and surface sources."
  },
  solution: {
    meta_title: "Dark Web Monitoring & Cyber Threat Intelligence Solution | DarknetSearch",
    meta_description: "Protect your organization with DarknetSearch, a real-time dark web monitoring and cyber threat intelligence solution that detects leaks before attackers do."
  },
  attack_surface_discover: {
    meta_title: "Attack Surface Discovery & External Exposure Monitoring | DarknetSearch",
    meta_description: "Attack surface discovery solution, gain full visibility into your internet-exposed assets and reduce cyber risk with DarknetSearch’s external exposure monitoring."
  },
  domain_spoofing_detection: {
    meta_title: "Domain Spoofing Detection & Brand Impersonation Solution | DarknetSearch",
    meta_description: "Domain spoofing detection solution, protect your brand from impersonation, phishing and fake domains with DarknetSearch’s advanced threat intelligence."
  },
  data_breach_detection: {
    meta_title: "Data Breach Detection & Dark Web Leak Monitoring | DarknetSearch",
    meta_description: "Data breach detection solution, identify and mitigate leaked credentials, corporate data and dark web exposures with DarknetSearch’s advanced monitoring platform."
  },
  darknet_monitoring_mssp: {
    meta_title: "Dark Web Monitoring for MSSP & Cyber Threat Intelligence | DarknetSearch",
    meta_description: "Dark web monitoring solution for MSSPs, deliver real-time dark web intelligence, detect client data leaks and enhance threat visibility with DarknetSearch."
  },
  pricing: {
    meta_title: "Pricing & Plans | DarknetSearch — Dark Web Monitoring Made Simple",
    meta_description: "Transparent pricing for DarknetSearch — choose the right plan for dark web monitoring, threat intelligence and cyber brand protection."
  },
  use_cases: {
    meta_title: "Use Cases | DarknetSearch — Real-World Dark Web Intelligence Applications",
    meta_description: "Explore DarknetSearch use cases — from data breach detection to domain spoofing defense and cyber threat intelligence for MSSPs and enterprises."
  },
  knowledge: {
    meta_title: "Knowledge Center | DarknetSearch — Learn About Dark Web Intelligence",
    meta_description: "Explore the DarknetSearch Knowledge Center — guides, insights and tutorials on dark web monitoring, data breach detection and cyber threat intelligence."
  },
  about_us: {
    meta_title: "About Us | DarknetSearch — Innovating Dark Web Intelligence & Security",
    meta_description: "Learn about DarknetSearch — a leading cybersecurity company providing dark web monitoring, data breach detection and threat intelligence solutions worldwide."
  },
  calendly: {
    meta_title: "Book a Demo | DarknetSearch — Explore Dark Web Monitoring in Action",
    meta_description: "Schedule a live demo with DarknetSearch — discover how our dark web monitoring and cyber threat intelligence solutions protect your organization in real time."
  },
  glossary: {
    meta_title: "Cybersecurity Glossary | DarknetSearch — Dark Web & Threat Intelligence Terms",
    meta_description: "Explore the DarknetSearch Cybersecurity Glossary — key definitions on dark web monitoring, data breaches, phishing, threat intelligence and online security."
  },
  ciso: {
    meta_title: "CISO Use Case | DarknetSearch — Dark Web Intelligence for Security Leaders",
    meta_description: "Discover how CISOs use DarknetSearch to monitor the dark web, detect data breaches and strengthen organizational cyber resilience with actionable intelligence."
  },
  threat: {
    meta_title: "Threat Intelligence Use Case | DarknetSearch — Dark Web Monitoring for Analysts",
    meta_description: "See how threat intelligence teams use DarknetSearch to uncover dark web leaks, monitor emerging threats and enhance situational awareness across organizations."
  },
  soc: {
    meta_title: "SOC Use Case | DarknetSearch — Dark Web Monitoring for Security Operations",
    meta_description: "Discover how SOC teams leverage DarknetSearch to detect dark web threats, analyze data breaches and strengthen real-time incident response capabilities."
  },
  finance_industry: {
    meta_title: "Finance Industry Use Case | DarknetSearch — Dark Web Intelligence for Financial Services",
    meta_description: "Finance industry use case – secure financial institutions with DarknetSearch’s dark web monitoring and threat intelligence: detect leaked banking credentials, protect SWIFT transactions, and prevent fraud."
  },
  healthcare_industry: {
    meta_title: "Healthcare Industry Use Case | DarknetSearch — Dark Web Intelligence for Healthcare",
    meta_description: "Healthcare industry use case – secure patient data and medical systems with DarknetSearch’s dark web monitoring, breach detection and threat intelligence."
  },
  ecommerce_industry: {
    meta_title: "eCommerce Industry Use Case | DarknetSearch — Dark Web Intelligence for Retail",
    meta_description: "eCommerce industry use case – protect your online store with DarknetSearch’s dark web monitoring and threat intelligence: detect leaked accounts, safeguard payments, boost customer trust."
  },
  media_industry: {
    meta_title: "Media Industry Use Case | DarknetSearch — Dark Web Intelligence for Media",
    meta_description: "Media industry use case – protect broadcasters, publishers and streaming platforms with DarknetSearch’s dark web monitoring, leak detection and brand protection."
  },
  government_industry: {
    meta_title: "Government & Public Sector Use Case | DarknetSearch — Dark Web Intelligence for Government",
    meta_description: "Government industry use case – safeguard public sector assets and critical infrastructure with DarknetSearch’s dark web monitoring and threat intelligence."
  },
  energy_industry: {
    meta_title: "Energy Industry Use Case | DarknetSearch — Dark Web Intelligence for Energy",
    meta_description: "Energy industry use case – protect power grids, oil & gas, renewables with DarknetSearch’s dark web monitoring and threat intelligence."
  },
  technology_industry: {
    meta_title: "Technology Industry Use Case | DarknetSearch — Dark Web Intelligence for Tech",
    meta_description: "Technology industry use case – protect software & hardware firms with DarknetSearch’s dark web monitoring and threat intelligence: prevent leaks, safeguard IP, stay ahead."
  },
  education_industry: {
    meta_title: "Education Industry Use Case | DarknetSearch — Dark Web Intelligence for Education",
    meta_description: "Education industry use case – protect schools, colleges and universities with DarknetSearch’s dark web monitoring and threat intelligence: detect leaks, safeguard student data, support safe learning environments."
  },
  legal_industry: {
    meta_title: "Legal Industry Use Case | DarknetSearch — Dark Web Intelligence for Law Firms",
    meta_description: "Legal industry use case – protect law firms and legal teams with DarknetSearch’s dark web monitoring and threat intelligence: detect leaks, safeguard client data, stay compliant."
  },
  insurance_industry: {
    meta_title: "Insurance Industry Use Case | DarknetSearch — Dark Web Intelligence for Insurance",
    meta_description: "Insurance industry use case – protect insurers and brokers with DarknetSearch’s dark web monitoring and threat intelligence: detect leaked policyholder data and prevent cyber claims."
  },
  customer_success_stories: {
    meta_title: "Success Stories | Intelligence Hub | DarknetSearch",
    meta_description: "IDiscover how global organizations use DarknetSearch intelligence to detect data leaks early, reduce exposure, and prevent breaches."
  },
  partners: {
    meta_title: "MSSP Startup Partner Program | Build Your Cybersecurity Business with DarknetSearch",
    meta_description: "Launch your MSSP with DarknetSearch — the partner platform for startups offering dark web monitoring, breach detection, and compliance-ready intelligence."
  },
  "free-trial": {
    meta_title: "Free Trial | DarknetSearch - 7-Day Full Access",
    meta_description: "Start a 7-day free trial of DarknetSearch to explore live dark web monitoring, alerts, and reports before you buy."
  },
  "cyber-laws": {
    meta_title: "Global Cybersecurity Laws & Data Privacy Regulations by Country",
    meta_description: "Global cybersecurity and data privacy laws explained by region. Understand GDPR, NIS2, US, APAC and more to stay compliant and reduce risk."
  },
}



const SEO = ({ 
  title, 
  description, 
  lang = "en", 
  hadCanonical = true,
  keywords,
  author = "DarknetSearch",
  image,
  type = "website",
  page_seo_content,
  hreflangs, // optional custom hreflang set
}) => {
  // Get default content based on language
  const getDefaultContent = (language) => {
    return DEFAULT_SEO_CONTENT[language] || DEFAULT_SEO_CONTENT.en;
  };

  const defaultContent = getDefaultContent(lang);
  if (page_seo_content){
    title = PAGE_SEO_CONTENT[page_seo_content].meta_title;
    description = PAGE_SEO_CONTENT[page_seo_content].meta_description;
  }
  // Use provided props or fall back to defaults
  const seoTitle = title || defaultContent.title;
  const seoDescription = description || defaultContent.description;
  const seoLang = lang === "glossary" ? "en" : lang;
  const canonicalUrl = `${BASE_URL}${typeof window !== 'undefined' ? window.location.pathname : ''}`;
  const seoImage = image || `${BASE_URL}/logo512.png`;

  // Default hreflang set for locale hubs; can be overridden via hreflangs prop
  const defaultHreflangs =
    hreflangs ||
    (["es", "fr", "de"].includes(lang)
      ? [
          { href: `${BASE_URL}/es`, lang: "es" },
          { href: `${BASE_URL}/fr`, lang: "fr" },
          { href: `${BASE_URL}/de`, lang: "de" },
          { href: `${BASE_URL}/`, lang: "x-default" },
        ]
      : null);

  // Generate structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "DarknetSearch",
    "alternateName": "DarknetSearch",
    "url": BASE_URL,
    "description": seoDescription,
    "publisher": {
      "@type": "Organization",
      "name": "DarknetSearch",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo512.png`
      }
    }
  };

  return (
    <Helmet>
      {/* Basic HTML meta tags */}
      <html lang={seoLang} />
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="application-name" content="DarknetSearch" />
      
      {/* Additional meta tags */}
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      <meta name="robots" content="index, follow" />
      <meta name="language" content={seoLang} />
      <meta name="revisit-after" content="7 days" />
      
      {/* Canonical URL */}
      {hadCanonical && <link rel="canonical" href={canonicalUrl} />}
      {defaultHreflangs &&
        defaultHreflangs.map((alt) => (
          <link
            key={alt.lang}
            rel="alternate"
            hrefLang={alt.lang}
            href={alt.href}
          />
        ))}
      
      {/* PWA manifest */}
      <link rel="manifest" href="/manifest.json" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content="DarknetSearch" />
      <meta property="og:locale" content={seoLang === 'en' ? 'en_US' : `${seoLang}_${seoLang.toUpperCase()}`} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={seoTitle} />
      <meta property="twitter:description" content={seoDescription} />
      <meta property="twitter:image" content={seoImage} />
      <meta property="twitter:site" content="@darknetsearch" />
      
      {/* Additional link tags */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />
      
      {/* Structured data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
